import React from 'react';
import { useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { getCurrentUserTypeRoles } from '../../util/userHelpers';
import { NamedRedirect } from '../../components';

/**
 * Resolve the inbox tab that actually has something in it for the current user.
 *
 * The inbox is split into orders (things you bought) and sales (things you
 * sold). Which one is the sensible landing tab depends on the roles the user's
 * user type grants — mirroring the same choice the topbar's inbox link makes.
 *
 * @param {Object} config marketplace configuration
 * @param {Object} currentUser current user entity, may be undefined before load
 * @param {boolean} currentUserHasListings whether the user has any listings
 * @returns {'orders'|'sales'} the tab to redirect to
 */
export const resolveInboxTab = (config, currentUser, currentUserHasListings) => {
  const { customer: isCustomer, provider: isProvider } = getCurrentUserTypeRoles(
    config,
    currentUser
  );

  return !isCustomer
    ? 'sales'
    : !isProvider
    ? 'orders'
    : currentUserHasListings
    ? 'sales'
    : 'orders';
};

/**
 * Redirect /inbox to the tab that fits the signed-in user.
 *
 * Note that the current user may not have loaded yet on the very first render.
 * `getCurrentUserTypeRoles` then reports both roles, and we fall back to
 * orders — the safe default, since every user can buy but only sellers sell.
 *
 * @component
 * @returns {JSX.Element} redirect to the InboxPage
 */
const InboxTabRedirect = () => {
  const config = useConfiguration();
  const currentUser = useSelector(state => state.user.currentUser);
  const currentUserHasListings = useSelector(state => state.user.currentUserHasListings);

  const tab = resolveInboxTab(config, currentUser, currentUserHasListings);
  return <NamedRedirect name="InboxPage" params={{ tab }} />;
};

export default InboxTabRedirect;
